// DataState — one shared primitive for the distinct non-content states of a data
// surface, so pages stop collapsing error / permission-denied / feature-unavailable
// into a single blank "empty" render (US-17/18/26). Pair it with Skeleton for
// `loading` where a skeleton is more appropriate than a spinner.
//
//   loading      — the query is in flight
//   empty        — the query succeeded and returned zero rows
//   error        — the query failed (network/5xx); offers Retry
//   denied       — the caller lacks permission (403)
//   unavailable  — the feature isn't enabled/entitled for this tenant
import React from 'react';
import { AlertTriangle, Ban, Inbox, Loader2, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';

export type DataStateVariant = 'loading' | 'empty' | 'error' | 'denied' | 'unavailable';

interface DataStateProps {
  variant: DataStateVariant;
  title?: string;
  message?: string;
  /** Shown only on `error`; wires a bounded, manual Retry (never an auto-loop). */
  onRetry?: () => void;
  retryLabel?: string;
  /** Compact mode for table cells / tight rows (smaller, single line). */
  compact?: boolean;
  /** Optional action (e.g. a "Connect Asana" / "View plans" link) for denied/unavailable/empty. */
  action?: React.ReactNode;
  className?: string;
}

const META: Record<DataStateVariant, { icon: React.ComponentType<{ className?: string }>; title: string; tone: string }> = {
  loading: { icon: Loader2, title: 'Loading…', tone: 'text-muted-foreground' },
  empty: { icon: Inbox, title: 'Nothing here yet', tone: 'text-muted-foreground' },
  error: { icon: AlertTriangle, title: 'Couldn’t load this', tone: 'text-danger-strong' },
  denied: { icon: Lock, title: 'No access', tone: 'text-muted-foreground' },
  unavailable: { icon: Ban, title: 'Not available', tone: 'text-muted-foreground' },
};

export const DataState: React.FC<DataStateProps> = ({
  variant, title, message, onRetry, retryLabel = 'Retry', compact, action, className,
}) => {
  const m = META[variant];
  const Icon = m.icon;
  const spin = variant === 'loading' ? 'animate-spin' : '';
  if (compact) {
    return (
      <div className={`flex items-center gap-2 py-3 px-4 text-sm ${m.tone} ${className || ''}`}>
        <Icon className={`w-4 h-4 shrink-0 ${spin}`} />
        <span>{message || title || m.title}</span>
        {variant === 'error' && onRetry && (
          <button type="button" onClick={onRetry} className="ml-1 inline-flex items-center gap-1 text-primary-500 hover:underline">
            <RefreshCw className="w-3 h-3" />{retryLabel}
          </button>
        )}
        {action}
      </div>
    );
  }
  return (
    <div className={`flex flex-col items-center justify-center text-center gap-2 py-10 px-6 ${className || ''}`}>
      <Icon className={`w-6 h-6 ${m.tone} ${spin}`} />
      <p className={`text-sm font-medium ${variant === 'error' ? 'text-danger-strong' : 'text-foreground'}`}>{title || m.title}</p>
      {message && <p className="text-sm text-muted-foreground max-w-sm">{message}</p>}
      {variant === 'error' && onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} className="mt-1">
          <RefreshCw className="w-4 h-4 mr-1" />{retryLabel}
        </Button>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
};

/** Map an axios/fetch-style error to the right non-loading DataState variant. */
export function errorVariant(err: unknown): 'error' | 'denied' {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 403 || status === 401 ? 'denied' : 'error';
}
